// src/app/features/legal-information/questionnaire/questions.config.ts

export type QuestionType =
  | 'text-yesno'        // Q1
  | 'chips-yesno'        // Q2, Q8
  | 'parties'            // Q3
  | 'textarea'           // Q4, Q10
  | 'events'             // Q5
  | 'documents'          // Q6
  | 'chips-radio';       // Q7

export interface QuestionOption {
  label: string;
}

export interface QuestionConfig {
  step: number;
  key: string;                 // key into PreReadAnswers driving this step's "known/have" boolean
  questionCode: string;        // backend question_code (PRD Part 4/7) — see pre-read.model.ts QUESTION_CODES
  type: QuestionType;
  title: string;
  helpText?: string;
  yesLabel?: string;
  noLabel?: string;
  optionsFieldLabel?: string;
  options?: string[];
  otherOptionLabel?: string;   // e.g. 'Other'
  wordLimit?: number;
  required?: boolean;          // whether the free-text/detail is mandatory once "yes" is chosen
  placeholder?: string;
  voiceInput?: boolean;
}

export const QUESTIONNAIRE_TOTAL_STEPS = 10;

export const QUESTIONS: QuestionConfig[] = [
  {
    step: 1,
    key: 'hasCaseTitle',
    questionCode: 'HAS_CASE_TITLE',
    type: 'text-yesno',
    title: 'Do you have a name or title for this legal issue?',
    helpText: 'e.g., Property Dispute with ABC Builders, Divorce Proceedings, Employment Termination Issue',
    yesLabel: 'Yes, I have',
    noLabel: "No, I don't",
    wordLimit: 150,
    placeholder: 'Case title',
    voiceInput: true
  },
  {
    step: 2,
    key: 'knowsIssueType',
    questionCode: 'LEGAL_ISSUE_TYPE',
    type: 'chips-yesno',
    title: 'Do you know what type of legal issue this is?',
    helpText: "Select all that apply. If none fit, choose 'Other' and specify.",
    yesLabel: 'Yes, I do',
    noLabel: "No, I don't",
    optionsFieldLabel: 'Legal issue type (select all that apply)',
    options: ['Arbitration', 'Civil', 'Criminal', 'Consumer', 'Corporate', 'Employment', 'Family', 'Property', 'Tax'],
    otherOptionLabel: 'Other',
    wordLimit: 50
  },
  {
    step: 3,
    key: 'knowsOpposingParty',
    questionCode: 'OPPOSITE_PARTY',
    type: 'parties',
    title: 'Do you know your opposing party?',
    helpText: 'You can add multiple entries. Add all parties on the other side of this issue.',
    yesLabel: 'Yes, I do',
    noLabel: "No, I don't"
  },
  {
    step: 4,
    key: 'incidentDescription',
    questionCode: 'INCIDENT_DESCRIPTION',
    type: 'textarea',
    title: 'Can you describe what happened?',
    helpText: 'Start from the beginning and explain the details of series of events associated with the issue. Include names, dates, places, and any important facts.',
    placeholder: 'Start from the beginning...',
    wordLimit: 500,
    required: true,
    voiceInput: true
  },
  {
    step: 5,
    key: 'events',
    questionCode: 'IMPORTANT_DATES',
    type: 'events',
    title: 'Can you add the events associated with this issue?',
    helpText: 'Add key dates and events in chronological order.'
  },
  {
    step: 6,
    key: 'hasDocuments',
    questionCode: 'SUPPORTING_DOCUMENTS', // backend current_step for this PATCH is 6 — see toBackendCurrentStep()
    type: 'documents',
    title: 'Do you have any documents supporting this issue?',
    yesLabel: 'Yes, I do',
    noLabel: "No, I don't",
    options: ['Agreements', 'Audio/Video', 'Contracts', 'Court orders', 'Emails', 'FIRs', 'Legal notices', 'Other evidence', 'Photographs', 'Other']
  },
  {
    step: 7,
    key: 'wantsOutcomeOptions',
    questionCode: 'EXPECTED_OUTCOME',
    type: 'chips-radio',
    title: 'What do you expect from the lawyer?',
    yesLabel: 'Let me see options',
    noLabel: "I don't know yet",
    optionsFieldLabel: 'Expected outcomes (select all that apply)',
    options: ['Bail', 'Compensation', 'Divorce', 'Get possession of property', 'Obtain an injunction', 'Quash proceedings', 'Recover money'],
    otherOptionLabel: 'Other',
    wordLimit: 50
  },
  {
    step: 8,
    key: 'legalActionTaken',
    questionCode: 'LEGAL_ACTION_TAKEN',
    type: 'chips-yesno',
    title: 'Has any legal action already been taken?',
    yesLabel: 'Yes',
    noLabel: "No, I don't know",
    optionsFieldLabel: 'What action was taken? (select all that apply)',
    options: ['Appeal Filed', 'Case Already Pending', 'Complaint Filed', 'FIR Filed', 'Legal Notice Sent'],
    otherOptionLabel: 'Other',
    wordLimit: 50
  },
  {
    step: 9,
    key: 'knowsDeadlines',
    questionCode: 'URGENT_DEADLINES',
    type: 'chips-yesno', // deadlines get a dedicated template block, see component
    title: 'Do you know if there are any upcoming deadlines or hearing dates?',
    yesLabel: 'Yes, I do',
    noLabel: "No, I don't know",
    optionsFieldLabel: 'Select deadline types (select all that apply)',
    options: ['Filing deadline', 'Hearing date', 'Limitation deadline', 'Notice reply deadline', 'Other']
  },
  {
    step: 10,
    key: 'additionalInformation',
    questionCode: 'ADDITIONAL_INFORMATION',
    type: 'textarea',
    title: 'Any additional information?',
    helpText: 'Anything else your lawyer should know. This field is optional.',
    placeholder: 'Add any further context or details here...',
    wordLimit: 500,
    required: false,
    voiceInput: true
  }
];